import React from 'react';
import {
  Box,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Tooltip,
  Paper,
  FormControl,
  Autocomplete,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { SimpleRule, RuleColumn, operatorOptions } from '../../types/leadSourceRules';

interface RuleRowProps {
  rule: SimpleRule;
  columns: RuleColumn[];
  index: number;
  totalRules: number;
  onChange: (rule: SimpleRule) => void;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onCopy: () => void;
}

export const RuleRow: React.FC<RuleRowProps> = ({
  rule,
  columns,
  index,
  totalRules,
  onChange,
  onDelete,
  onMoveUp,
  onMoveDown,
  onCopy,
}) => {
  const selectedColumn = columns.find((c) => c.column_name === rule.column);
  const selectedOperator = operatorOptions.find((o) => o.value === rule.operator);
  const needsValue = selectedOperator?.needsValue !== false;

  // Get sample values for autocomplete
  const sampleValues = selectedColumn?.sample_values || [];

  const handleColumnChange = (columnName: string) => {
    onChange({
      ...rule,
      column: columnName,
      value: '',
    });
  };

  const isInOperator = rule.operator === 'IN' || rule.operator === 'NOT IN';

  const handleOperatorChange = (operator: SimpleRule['operator']) => {
    const newOp = operatorOptions.find((o) => o.value === operator);
    const wasIn = isInOperator;
    const nowIn = operator === 'IN' || operator === 'NOT IN';
    let newValue: SimpleRule['value'] = rule.value;
    if (newOp?.needsValue === false) {
      newValue = null;
    } else if (wasIn && !nowIn) {
      // Convert array back to single string
      newValue = Array.isArray(rule.value) ? rule.value.join(', ') : rule.value;
    } else if (!wasIn && nowIn) {
      // Convert single string to array
      const str = rule.value?.toString() || '';
      newValue = str
        ? str
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)
        : [];
    }
    onChange({ ...rule, operator, value: newValue });
  };

  const handleValueChange = (value: string | number | null) => {
    onChange({
      ...rule,
      value,
    });
  };

  const handleInValueChange = (value: string) => {
    const items = value
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    onChange({ ...rule, value: items });
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: 1,
        backgroundColor: '#fafafa',
        '&:hover': {
          backgroundColor: '#f5f5f5',
        },
      }}
    >
      {/* Column Select */}
      <FormControl size="small" sx={{ minWidth: 180 }}>
        <Select
          value={rule.column}
          onChange={(e) => handleColumnChange(e.target.value)}
          displayEmpty
        >
          {columns.map((col) => (
            <MenuItem key={col.column_name} value={col.column_name}>
              <Box>
                <Box component="span">{col.display_name}</Box>
                <Box component="span" sx={{ ml: 1, color: 'text.secondary', fontSize: '0.75rem' }}>
                  ({col.column_name})
                </Box>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Operator Select */}
      <FormControl size="small" sx={{ minWidth: 160 }}>
        <Select
          value={rule.operator}
          onChange={(e) => handleOperatorChange(e.target.value as SimpleRule['operator'])}
        >
          {operatorOptions.map((op) => (
            <MenuItem key={op.value} value={op.value}>
              {op.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Value Input */}
      {needsValue && isInOperator && (
        <TextField
          size="small"
          value={Array.isArray(rule.value) ? rule.value.join(', ') : rule.value?.toString() || ''}
          onChange={(e) => handleInValueChange(e.target.value)}
          placeholder="value1, value2, value3"
          helperText="Comma-separated values"
          FormHelperTextProps={{ sx: { mt: 0, mb: -1 } }}
          sx={{ flex: 1, minWidth: 200 }}
        />
      )}
      {needsValue && !isInOperator && (
        <Autocomplete
          freeSolo
          size="small"
          options={sampleValues}
          value={rule.value?.toString() || ''}
          onInputChange={(_, newValue) => handleValueChange(newValue)}
          sx={{ flex: 1, minWidth: 200 }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Value"
              size="small"
              helperText={
                rule.operator === 'LIKE' || rule.operator === 'NOT LIKE'
                  ? 'Use % as wildcard'
                  : undefined
              }
              FormHelperTextProps={{ sx: { mt: 0, mb: -1 } }}
            />
          )}
        />
      )}

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', ml: 'auto' }}>
        <Tooltip title="Move Up">
          <span>
            <IconButton size="small" onClick={onMoveUp} disabled={index === 0}>
              <ArrowUpIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Move Down">
          <span>
            <IconButton size="small" onClick={onMoveDown} disabled={index === totalRules - 1}>
              <ArrowDownIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Copy">
          <IconButton size="small" onClick={onCopy}>
            <CopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton size="small" onClick={onDelete} color="error">
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Paper>
  );
};
